import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Drawer from "@mui/material/Drawer";
import TiersAddComponent from "./TiersAdd";
import SponsorAddComponent from "./SponsorAdd";
import SponsorListComponent from "./SponsorList";
import { useParams } from "react-router-dom";
import { axiosInstance } from "../../../../utils/axios";

import {
  Box,
  Button,
  Divider as MuiDivider,
  Grid,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon } from "@mui/icons-material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

function Sponsors() {
  const { id: eventId } = useParams();

  // SEPARATE STATES FOR TIERS AND SPONSORS
  const [tiersDrawerOpen, setTiersDrawerOpen] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [tiersError, setTiersError] = useState("");
  const [tiersSuccess, setTiersSuccess] = useState("");

  const [sponsorDrawerOpen, setSponsorDrawerOpen] = useState(false);
  const [sponsors, setSponsors] = useState([]);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [sponsorError, setSponsorError] = useState("");
  const [sponsorSuccess, setSponsorSuccess] = useState("");
  const [editingSponsor, setEditingSponsor] = useState(null);

  // LOAD EVENT DATA
  const loadEventData = async () => {
    if (!eventId) return;

    console.log("🔄 Loading event data for eventId:", eventId);
    setTiersLoading(true);
    setSponsorLoading(true);
    setTiersError("");
    setSponsorError("");

    try {
      const query = `
        query GetEvent($eventId: ID!) {
          event(id: $eventId) {
            id
            name
            sponsorTiers {
              id
              name
            }
            sponsors {
              id
              name
              website
              tier
              logo
            }
          }
        }
      `;

      const response = await axiosInstance.post("/graphql", {
        query,
        variables: { eventId },
      });

      console.log("🔍 Load event - Full response:", response);
      console.log("🔍 Load event - response.data:", response.data);

      // Handle GraphQL errors
      if (response.data?.errors) {
        const errorMsg =
          response.data.errors[0]?.message || "Failed to load event data";
        setTiersError(errorMsg);
        setSponsorError(errorMsg);
        return;
      }

      // Extract event data - try multiple possible structures
      let eventData = null;
      if (response.data?.data?.event) {
        eventData = response.data.data.event;
      } else if (response.data?.event) {
        eventData = response.data.event;
      }

      if (eventData) {
        console.log("📊 Event data loaded:", eventData);
        setTiers(eventData.sponsorTiers || []);
        setSponsors(eventData.sponsors || []);
      } else {
        console.error("❌ No event data found in response");
        setTiersError("No event data found");
        setSponsorError("No event data found");
      }
    } catch (err) {
      console.error("❌ Error loading event data:", err);
      const errorMsg = "Failed to load event data: " + err.message;
      setTiersError(errorMsg);
      setSponsorError(errorMsg);
    } finally {
      setTiersLoading(false);
      setSponsorLoading(false);
    }
  };

  useEffect(() => {
    loadEventData();
  }, [eventId]);

  // ============= TIER FUNCTIONS (SEPARATE) =============
  const handleSaveTiers = async (newTiers) => {
    if (!eventId) return;

    setTiersLoading(true);
    setTiersError("");
    setTiersSuccess("");

    try {
      const mutation = `
        mutation SaveSponsorTiers($eventId: ID!, $tiers: [SponsorTierInput!]!) {
          saveSponsorTiers(eventId: $eventId, tiers: $tiers) {
            id
            name
          }
        }
      `;

      const tierInputs = newTiers.map((tier) => ({ name: tier.name }));

      const response = await axiosInstance.post("/graphql", {
        query: mutation,
        variables: { eventId, tiers: tierInputs },
      });

      console.log("🏷️ Save tiers - Full response:", response);
      console.log("🏷️ Save tiers - response.data:", response.data);

      // Handle GraphQL errors
      if (response.data?.errors) {
        const errorMsg =
          response.data.errors[0]?.message || "Failed to save tiers";
        setTiersError(errorMsg);
        return;
      }

      // Extract tiers data - try multiple possible structures
      let savedTiers = null;
      if (response.data?.data?.saveSponsorTiers) {
        savedTiers = response.data.data.saveSponsorTiers;
      } else if (response.data?.saveSponsorTiers) {
        savedTiers = response.data.saveSponsorTiers;
      }

      if (savedTiers) {
        setTiers(savedTiers);
        setTiersSuccess("Tiers saved successfully!");
        setTimeout(() => setTiersSuccess(""), 3000);
      } else {
        setTiersError("Failed to save tiers - no data returned");
      }
    } catch (err) {
      console.error("❌ Error saving tiers:", err);
      setTiersError("Failed to save tiers: " + err.message);
    } finally {
      setTiersLoading(false);
    }
  };

  // ============= SPONSOR FUNCTIONS (SEPARATE) =============
  const addSponsor = async (sponsorData) => {
    const mutation = `
      mutation AddSponsor($eventId: ID!, $sponsor: SponsorInput!) {
        addSponsor(eventId: $eventId, sponsor: $sponsor) {
          success
          message
          sponsor {
            id
            name
            website
            tier
            logo
          }
        }
      }
    `;

    const response = await axiosInstance.post("/graphql", {
      query: mutation,
      variables: {
        eventId,
        sponsor: {
          name: sponsorData.name,
          website: sponsorData.website || "",
          tier: sponsorData.tier,
          logo: sponsorData.logo || "",
        },
      },
    });

    console.log("➕ Add sponsor - Full response:", response);
    console.log("➕ Add sponsor - response.data:", response.data);

    // Handle GraphQL errors
    if (response.data?.errors) {
      throw new Error(
        response.data.errors[0]?.message || "Failed to add sponsor"
      );
    }

    // Extract result - try multiple possible structures
    let result = null;
    if (response.data?.data?.addSponsor) {
      result = response.data.data.addSponsor;
    } else if (response.data?.addSponsor) {
      result = response.data.addSponsor;
    }

    console.log("➕ Add sponsor result:", result);

    if (!result || !result.success) {
      throw new Error(result?.message || "Failed to add sponsor");
    }

    return result;
  };

  const updateSponsor = async (sponsorData) => {
    const mutation = `
      mutation UpdateSponsor($eventId: ID!, $sponsorId: ID!, $sponsor: SponsorInput!) {
        updateSponsor(eventId: $eventId, sponsorId: $sponsorId, sponsor: $sponsor) {
          success
          message
          sponsor {
            id
            name
            website
            tier
            logo
          }
        }
      }
    `;

    const response = await axiosInstance.post("/graphql", {
      query: mutation,
      variables: {
        eventId,
        sponsorId: editingSponsor.id,
        sponsor: {
          name: sponsorData.name,
          website: sponsorData.website || "",
          tier: sponsorData.tier,
          logo: sponsorData.logo || "",
        },
      },
    });

    console.log("✏️ Update sponsor - Full response:", response);
    console.log("✏️ Update sponsor - response.data:", response.data);

    // Handle GraphQL errors
    if (response.data?.errors) {
      throw new Error(
        response.data.errors[0]?.message || "Failed to update sponsor"
      );
    }

    // Extract result - try multiple possible structures
    let result = null;
    if (response.data?.data?.updateSponsor) {
      result = response.data.data.updateSponsor;
    } else if (response.data?.updateSponsor) {
      result = response.data.updateSponsor;
    }

    console.log("✏️ Update sponsor result:", result);

    if (!result || !result.success) {
      throw new Error(result?.message || "Failed to update sponsor");
    }

    return result;
  };

  const handleSaveSponsor = async (sponsorData) => {
    setSponsorError("");
    setSponsorSuccess("");

    try {
      console.log("💾 Starting save sponsor process");
      console.log("💾 Is edit mode:", !!editingSponsor);
      console.log("💾 Sponsor data:", sponsorData);

      if (editingSponsor) {
        // Update existing sponsor
        const result = await updateSponsor(sponsorData);
        console.log("✅ Update sponsor result:", result);
        setSponsorSuccess("Sponsor updated successfully!");
      } else {
        // Add new sponsor
        const result = await addSponsor(sponsorData);
        console.log("✅ Add sponsor result:", result);
        setSponsorSuccess("Sponsor added successfully!");
      }

      // Reload data and close modal
      console.log("🔄 Reloading event data...");
      await loadEventData();
      setSponsorDrawerOpen(false);
      setEditingSponsor(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSponsorSuccess(""), 3000);
    } catch (err) {
      console.error("❌ Error saving sponsor:", err);
      setSponsorError(err.message);
    }
  };

  const handleEditSponsor = (sponsor) => {
    console.log("✏️ Edit sponsor:", sponsor);
    setEditingSponsor(sponsor);
    setSponsorDrawerOpen(true);
  };

  const handleDeleteSponsors = async (sponsorIds) => {
    setSponsorError("");
    setSponsorSuccess("");

    try {
      for (const sponsorId of sponsorIds) {
        const mutation = `
          mutation DeleteSponsor($eventId: ID!, $sponsorId: ID!) {
            deleteSponsor(eventId: $eventId, sponsorId: $sponsorId)
          }
        `;

        const response = await axiosInstance.post("/graphql", {
          query: mutation,
          variables: { eventId, sponsorId },
        });

        if (response.data?.errors) {
          throw new Error(
            response.data.errors[0]?.message || "Failed to delete sponsor"
          );
        }
      }

      // Reload data
      await loadEventData();

      const count = sponsorIds.length;
      setSponsorSuccess(
        `Successfully deleted ${count} sponsor${count > 1 ? "s" : ""}!`
      );
      setTimeout(() => setSponsorSuccess(""), 3000);
    } catch (err) {
      console.error("❌ Error deleting sponsors:", err);
      setSponsorError("Failed to delete sponsors: " + err.message);
    }
  };

  const handleDownloadSponsors = () => {
    console.log("Downloading sponsors as CSV");

    // Prepare CSV headers
    const headers = ["Position", "Name", "Website", "Tier"];

    // Prepare CSV rows
    const csvRows = [
      headers.join(","), // Header row
      ...sponsors.map((sponsor, index) => {
        const row = [
          index + 1, // Position
          `"${sponsor.name || ""}"`, // Name (quoted to handle commas)
          `"${sponsor.website || ""}"`, // Website (quoted to handle commas)
          `"${sponsor.tier || ""}"`, // Tier (quoted to handle commas)
        ];
        return row.join(",");
      }),
    ];

    // Create CSV content
    const csvContent = csvRows.join("\n");

    // Create blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    // Set download attributes
    const fileName = `sponsors_${eventId}_${
      new Date().toISOString().split("T")[0]
    }.csv`;
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);

    // Show success message
    setSponsorSuccess(`Downloaded ${sponsors.length} sponsor(s) as CSV`);
    setTimeout(() => setSponsorSuccess(""), 3000);
  };

  return (
    <React.Fragment>
      {/* TIER ERROR AND SUCCESS MESSAGES */}
      {tiersError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Tiers:</strong> {tiersError}
        </Alert>
      )}

      {tiersSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Tiers:</strong> {tiersSuccess}
        </Alert>
      )}

      {/* SPONSOR ERROR AND SUCCESS MESSAGES */}
      {sponsorError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Sponsors:</strong> {sponsorError}
        </Alert>
      )}

      {sponsorSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Sponsors:</strong> {sponsorSuccess}
        </Alert>
      )}

      {/* LOADING INDICATORS */}
      {(tiersLoading || sponsorLoading) && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* HEADER */}
      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom>
            Sponsors
          </Typography>
          <Typography>Manage sponsors for your event</Typography>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setTiersDrawerOpen(true)}
            sx={{ mr: 1 }}
            disabled={tiersLoading}
          >
            <EditIcon />
            Manage Tiers
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              console.log(
                "🏷️ Opening new sponsor modal, tiers available:",
                tiers
              );
              if (tiers.length === 0) {
                setSponsorError(
                  "Please create sponsor tiers first before adding sponsors"
                );
                setTimeout(() => setSponsorError(""), 5000);
                return;
              }
              setEditingSponsor(null);
              setSponsorDrawerOpen(true);
            }}
            disabled={sponsorLoading}
          >
            <AddIcon />
            New Sponsor
          </Button>
        </Grid>
      </Grid>

      <Divider my={6} />

      {/* TIERS SECTION */}
      <Box mt={2} mb={4}>
        <Typography variant="h5" gutterBottom>
          Tiers ({tiers.length})
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {tiers.length > 0 ? (
            tiers.map((tier, index) => (
              <Chip
                key={tier.id || index}
                label={`${index + 1}. ${tier.name}`}
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No tiers created yet. Click "Manage Tiers" to add some.
            </Typography>
          )}
        </Stack>
      </Box>

      <Divider my={6} />

      {/* SPONSORS LIST */}
      <Typography variant="h5" gutterBottom>
        Sponsors ({sponsors.length})
      </Typography>
      <SponsorListComponent
        sponsors={sponsors}
        onEdit={handleEditSponsor}
        onDelete={handleDeleteSponsors}
        onDownload={handleDownloadSponsors}
      />

      {/* TIERS DRAWER */}
      <Drawer
        anchor="right"
        open={tiersDrawerOpen}
        onClose={() => setTiersDrawerOpen(false)}
      >
        <TiersAddComponent
          onClose={() => setTiersDrawerOpen(false)}
          onSave={handleSaveTiers}
          initialTiers={tiers}
        />
      </Drawer>

      {/* SPONSOR DRAWER */}
      <Drawer
        anchor="right"
        open={sponsorDrawerOpen}
        onClose={() => setSponsorDrawerOpen(false)}
      >
        <SponsorAddComponent
          onClose={() => {
            setSponsorDrawerOpen(false);
            setEditingSponsor(null);
          }}
          onSave={handleSaveSponsor}
          availableTiers={tiers}
          editingSponsor={editingSponsor}
          isEditMode={!!editingSponsor}
        />
      </Drawer>
    </React.Fragment>
  );
}

export default Sponsors;
